/**
 * Custom Hub Brand Registry
 *
 * Single source of truth for partner-branded custom hub routes (RTTR,
 * upcoming Polaris × CoD, etc.). Replaces the per-slug hardcoding that
 * was spread across main.tsx, HubPageRouter.tsx, and index.html with one
 * typed config object per campaign.
 *
 * **Current state (as of Sprints 4-5):** the registry IS wired. main.tsx
 * reads the matched campaign and mounts CustomHubShell. HubPageRouter
 * still has its own per-slug SLUG_LOADERS + CUSTOM_HUBS maps (separate
 * concern — chunk lazy-imports and per-page loader resolution). The
 * inline brand-isolation script in index.html carries a parallel
 * CUSTOM_HUBS array because the script runs BEFORE any JS bundle loads
 * and physically cannot import from this file.
 *
 * Adding a new campaign requires THREE coordinated edits:
 *   1. New brand-config module at `customHubs/<slug>/index.ts`
 *   2. Import + register here in `CUSTOM_HUB_REGISTRY`
 *   3. Parallel `CUSTOM_HUBS` array entry in `web/index.html` inline script
 *
 * Why a registry and not per-campaign config files spread elsewhere:
 * main.tsx, HubPageRouter, vite.config, and index.html all need to know
 * the list of registered slugs. Centralizing here means adding a campaign
 * is one config entry per concern rather than syncing across four files.
 * See knowledge/projects/active/custom-hub-platform-layer/BUILD_PLAN.md
 * Design Decision 1 for the full rationale.
 */

import React from "react";
import { CustomHubProps } from "@/pages/hub/custom/types";
import { MINELAB_RTTR_HUB_CONFIG } from "./minelab-rttr";
import { POLARIS_FULLTHROTTLE_HUB_CONFIG } from "./polaris-cod-fullthrottle";

/**
 * Shape of a single custom hub campaign's brand configuration.
 *
 * Each campaign that wants partner-isolated routing (route-level provider
 * strip + brand-isolated frame-1 loader + isolated chunk + per-campaign
 * asset preloads) registers one of these.
 */
export interface CustomHubBrandConfig {
    /**
     * URL slug for the campaign — the value that matches `:slug` in the
     * `/hub/:slug` route. Used as the registry key, the brand-CSS class
     * suffix, and the chunk-name suffix.
     *
     * Example: `"minelab-rttr"` → mounts on `/hub/minelab-rttr*`, gets
     * CSS class `customhub-route--minelab-rttr` (Sprint 5), gets chunk
     * `customhub-minelab-rttr` (Sprint 6).
     */
    slug: string;

    /**
     * Brand-isolated React loader shown while:
     *   1. `useGetHub(slug)` is resolving (~100-500ms cold)
     *   2. The lazy-imported hub component chunk is downloading
     *      (~200-1000ms cold)
     *
     * Must render without depending on any platform stylesheet — inline
     * styles only. The loader executes BEFORE the hub component's CSS
     * file has been parsed by the browser, so any class-based styling
     * here is invisible.
     */
    loaderComponent: React.FC;

    /**
     * Lazy-imported hub component. Rendered by HubPageRouter once both the
     * hub data is fetched and the chunk download resolves. Wrapped in
     * <Suspense fallback={loaderComponent}> at the consumer site.
     */
    lazyComponent: React.LazyExoticComponent<React.FC<CustomHubProps>>;

    /**
     * Industry fonts (or other partner-specific fonts) that should be
     * preloaded when the request path matches this campaign. Injected as
     * `<link rel="preload">` tags by the brand-isolation inline script in
     * index.html (Sprint 5). Path-gated so non-campaign routes don't pay
     * the request cost.
     *
     * Empty array = no preloads. Useful for campaigns that don't carry
     * partner fonts or that rely solely on platform-default fonts.
     */
    fontPreloadHrefs: ReadonlyArray<string>;

    /**
     * The `font/*` MIME type matching `fontPreloadHrefs`. Required to make
     * the preload work — a `<link rel="preload" as="font">` without
     * matching `type` is rejected by Chromium-based browsers.
     *
     * If different font files in the same campaign use different types
     * (some `.woff2`, some `.otf`), prefer splitting into two separate
     * preload bundles or aligning all weights to one format.
     */
    fontPreloadType: string;

    /**
     * True when this campaign mounts its OWN branded reward modal, so the
     * generic one must not double-render on its routes.
     *
     * Explicit rather than inferred from registration: being a custom hub and
     * owning your prize notifications are different claims, and a future
     * campaign that registers without its own modal would silently lose the
     * notification entirely — the user never sees they won.
     *
     * Replaces two hardcoded path regexes in `GlobalRewardNotifications`, one
     * per campaign (D.7).
     */
    rendersOwnRewardNotifications: boolean;

    /**
     * Hostnames this campaign owns outright, served standalone by the light
     * entry. Empty when it is only reachable at `/hub/<slug>`.
     */
    standaloneHostnames: ReadonlyArray<string>;
}

/**
 * Registered custom-hub campaigns.
 *
 * Keys are slugs and must exactly match `CustomHubBrandConfig.slug`. Order
 * is irrelevant — consumers index by slug, not iteration order.
 *
 * To onboard a new campaign:
 *   1. Create `web/src/customHubs/<slug>/index.ts` exporting the brand
 *      config object (loader component, lazy hub component, font preload
 *      list, useMinimalShell choice).
 *   2. Import here and add to this map.
 *   3. (Sprint 4-5 work) The path fork in main.tsx, the SLUG_LOADERS map
 *      in HubPageRouter, and the brand-isolation script in index.html
 *      will all automatically pick up the new entry.
 *
 * No registry validation runtime is currently done — TypeScript enforces
 * the shape at the call sites and at config-export time. If a future
 * campaign needs additional runtime checks (e.g. asset file existence,
 * font URL probes), add them here as a separate validation utility,
 * NOT inline in this declaration.
 */
export const CUSTOM_HUB_REGISTRY: Readonly<Record<string, CustomHubBrandConfig>> = {
    [MINELAB_RTTR_HUB_CONFIG.slug]: MINELAB_RTTR_HUB_CONFIG,
    [POLARIS_FULLTHROTTLE_HUB_CONFIG.slug]: POLARIS_FULLTHROTTLE_HUB_CONFIG,
};

/**
 * Convenience: list of registered slugs. Useful for the path-fork in
 * main.tsx (Sprint 4) and the brand-isolation inline script in index.html
 * (Sprint 5) when they iterate the registry rather than hardcoding slugs.
 */
export const CUSTOM_HUB_SLUGS: ReadonlyArray<string> = Object.keys(CUSTOM_HUB_REGISTRY);

/**
 * Returns true if the given URL path corresponds to a registered custom-
 * hub route. Used by the main.tsx path fork (Sprint 4) and the index.html
 * brand-isolation script (Sprint 5) to decide whether to take the
 * partner-route code path or the standard DevourPlay app path.
 *
 * Matches:
 *   - exact `/hub/<slug>`
 *   - `/hub/<slug>/...` (sub-paths)
 *
 * Note: query strings on `/hub/<slug>?...` are not pathname matches —
 * `window.location.pathname` never contains the `?` delimiter (that
 * lives on `.search`). The `pathname === base` branch covers the
 * "exact path with query string" case correctly.
 */
export function findRegisteredCustomHubSlug(pathname: string): string | undefined {
    return CUSTOM_HUB_SLUGS.find((slug) => {
        const base = `/hub/${slug}`;
        return pathname === base || pathname.startsWith(`${base}/`);
    });
}

/**
 * True when the route's campaign renders its own reward modal.
 *
 * Fails OPEN — an unregistered route returns false, so the generic modal shows.
 * A missed suppression double-renders a win; a wrong suppression loses it
 * silently, and only one of those is recoverable by the user.
 */
export function routeRendersOwnRewardNotifications(pathname: string): boolean {
    const slug = findRegisteredCustomHubSlug(pathname);
    /* On its own hostname a campaign sits at "/", so the path alone cannot identify it. */
    const brand = slug ? CUSTOM_HUB_REGISTRY[slug] : findCustomHubByHostname(window.location.hostname);
    return brand ? brand.rendersOwnRewardNotifications : false;
}

/** The campaign that owns this hostname outright, if any. */
export function findCustomHubByHostname(hostname: string): CustomHubBrandConfig | undefined {
    const host = hostname.toLowerCase();
    return Object.values(CUSTOM_HUB_REGISTRY).find((brand) => brand.standaloneHostnames.includes(host));
}
